/**
 * @fileoverview Mock backend for the invitation-acceptance endpoints.
 * @description Registers GET /identity/invitations/{token}/validate and POST
 * /identity/invitations/accept on the feature's shared mock adapter (see auth-mock-adapter.ts),
 * both reading/writing invitation-mock-store.ts, so the flow is testable without a backend
 * running. Like the real backend, every unusable link gets the same 400 / appCode 1040, and
 * accepting opens an activation challenge (verified by activation-mock.service.ts) instead of a
 * session. A no-op when VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { ACTIVATION_RESEND_COOLDOWN_SECONDS } from './activation-mock.service'
import { authMockAdapter } from './auth-mock-adapter'
import { acceptInvitationRecord, findUsableInvitation } from './invitation-mock-store'
import { buildMockErrorEnvelope, buildMockSuccessEnvelope } from './mock-envelope.utils'

import type {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  InvitationValidationResponse,
} from '@/features/auth/services/invitation.service'

const INVITATION_INVALID_ERROR = buildMockErrorEnvelope(
  400,
  '1040',
  'Invitation link is invalid or no longer usable.',
)

const VALIDATE_PATH = /\/identity\/invitations\/([^/]+)\/validate$/

if (authMockAdapter) {
  authMockAdapter.onGet(VALIDATE_PATH).reply((config) => {
    const token = decodeURIComponent(VALIDATE_PATH.exec(config.url ?? '')?.[1] ?? '')

    if (!findUsableInvitation(token)) {
      return [400, INVITATION_INVALID_ERROR]
    }

    const response: InvitationValidationResponse = { valid: true, nextStep: 'SET_PASSWORD' }
    return [200, buildMockSuccessEnvelope(response, 'Invitation is valid')]
  })

  authMockAdapter.onPost('/identity/invitations/accept').reply((config) => {
    const { token, newPassword, confirmPassword } = JSON.parse(
      config.data as string,
    ) as AcceptInvitationRequest
    const record = findUsableInvitation(token)

    if (!record) {
      return [400, INVITATION_INVALID_ERROR]
    }

    if (newPassword !== confirmPassword) {
      return [400, buildMockErrorEnvelope(400, '0001', 'Validation failed.')]
    }

    const challenge = acceptInvitationRecord(token, newPassword)

    if (!challenge) {
      return [400, INVITATION_INVALID_ERROR]
    }

    const response: AcceptInvitationResponse = {
      invitationAccepted: true,
      nextStep: 'VERIFY_ACTIVATION_MFA',
      challengeId: challenge.challengeToken,
      channel: 'EMAIL',
      maskedDestination: record.email.replace(/^(.).*(@.*)$/, '$1***$2'),
      expiresInSeconds: Math.round((challenge.expiresAt - Date.now()) / 1000),
      resendAvailableInSeconds: ACTIVATION_RESEND_COOLDOWN_SECONDS,
    }

    return [200, buildMockSuccessEnvelope(response, 'Invitation accepted')]
  })
}
